import { Stack, Typography } from '@mui/material';
import { useCallback } from 'react';

import { PlusIcon } from '@/components/icons/PlusIcon';
import { COLORS } from '@/styles/tokens/colors';

export interface AddItemStackItem<T> {
  label: string;
  value: T;
  disabled?: boolean;
}

interface AddItemStackProps<T> {
  items: AddItemStackItem<T>[];
  onClick: (value: T) => void;
}

export function AddItemStack<T>({ items, onClick }: AddItemStackProps<T>) {
  return (
    <Stack direction="column" spacing={0.5}>
      {items.map((item) => (
        <AddItemButton key={item.label} item={item} onClick={onClick} />
      ))}
    </Stack>
  );
}

interface AddItemButtonProps<T> {
  item: AddItemStackItem<T>;
  onClick: (value: T) => void;
}

function AddItemButton<T>({ item, onClick }: AddItemButtonProps<T>) {
  const { label, value, disabled } = item;

  const handleClick = useCallback(() => {
    onClick(value);
  }, [onClick, value]);

  return (
    <Stack
      component="button"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      onClick={handleClick}
      disabled={disabled}
      aria-label={`Add "${label}"`}
      sx={{
        border: `1px solid ${COLORS.NAVY[100]}`,
        borderRadius: 0.5,
        padding: '8px 8px 8px 12px',
        minHeight: '40px',
        backgroundColor: COLORS.PRIMITIVES.WHITE,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: COLORS.NAVY[50],
        },
        '&:active': {
          backgroundColor: COLORS.NAVY[50],
        },
        '&:disabled': {
          backgroundColor: COLORS.PRIMITIVES.WHITE,
          border: `1px solid ${COLORS.NAVY[100]}`,
          cursor: 'not-allowed',
        },
      }}
    >
      <Typography
        variant="body-xs"
        sx={{
          color: disabled ? COLORS.TEXT.DISABLED : COLORS.TEXT.PRIMARY,
          textAlign: 'left',
        }}
      >
        {label}
      </Typography>
      <PlusIcon
        size={16}
        strokeWidth={1.33}
        color={disabled ? COLORS.TEXT.DISABLED : COLORS.TEXT.PRIMARY}
      />
    </Stack>
  );
}
